import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, Building2, Compass } from 'lucide-react';
import { useWorkspace } from '../lib/workspace';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// CompSwitcher
// Workspace selector, lives at the top of the Sidebar. Dropdown of comps with
// status — scoped to the entry org when the user came in through an org's
// Discord (T07), with an "Explore all" door to lift the filter. Picking one
// updates the global workspace context.
//
// House language (12-portal-design-overhaul): no borders/dividers — raised
// fills + space; accent escapes only onto the active selection.
// ─────────────────────────────────────────────────────────────────────────────

const PANEL = 'rounded-lg bg-paper-panel shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]';

export function CompSwitcher({ collapsed }: { collapsed: boolean }) {
  const { activeComp, visibleComps, setActiveCompId, isLoading, entryOrg, clearEntryOrg, orgScoped } = useWorkspace();
  const comps = visibleComps;
  // Read the workspace's decision; do NOT re-derive it. This line used to be
  // its own copy of `entryOrg !== null && !isFounder` — the same expression
  // whose entryOrg precondition let a view-as preview render every customer
  // org's comps. One rule, one owner (lib/compScope.ts).
  const scoped = orgScoped;
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Cap the visible list — long active-comp lists were rendering unbounded.
  const PAGE = 10;
  const visible = showAll ? comps : comps.slice(0, PAGE);
  const hiddenCount = comps.length - visible.length;

  useEffect(() => {
    if (!open) { setShowAll(false); return; }
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Collapsed view — icon only, tooltip with active comp name
  if (collapsed) {
    return (
      <div
        className="mx-auto flex h-9 w-9 items-center justify-center rounded-md bg-white/[0.06] text-ink-secondary"
        title={activeComp?.name || activeComp?.id?.replace('COMP#', '') || 'No comp'}
      >
        <Building2 className="h-4 w-4" strokeWidth={2.5} />
      </div>
    );
  }

  const label = activeComp?.name || activeComp?.id?.replace('COMP#', '') || (isLoading ? 'Loading…' : 'Pick a comp');

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg bg-white/[0.05] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.08]"
      >
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-ink-meta">Workspace</p>
          <p className="mt-0.5 truncate font-display text-xs font-black uppercase tracking-wider text-ink-primary">
            {label}
          </p>
        </div>
        <ChevronDown
          className={cn('h-3.5 w-3.5 shrink-0 text-ink-meta transition-transform', open && 'rotate-180')}
          strokeWidth={2.5}
        />
      </button>

      {open && (
        <div className={cn('absolute left-0 top-full z-50 mt-2 max-h-[60vh] w-full overflow-y-auto', PANEL)}>
          <div className="px-3 py-2.5">
            <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink-meta">
              {scoped && entryOrg ? `${entryOrg.orgName} workspaces` : 'Select workspace'}
            </p>
          </div>
          <div className="px-1.5 pb-1.5">
            {comps.length === 0 && (
              <p className="px-3 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-meta">
                No comps available
              </p>
            )}
            {visible.map(c => {
              const active = c.id === activeComp?.id;
              return (
                <button
                  key={c.id}
                  onClick={() => {
                    setActiveCompId(c.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'group flex w-full items-center justify-between gap-2 rounded-md px-3 py-2.5 text-left transition-colors',
                    active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'truncate font-display text-xs font-black uppercase tracking-wider',
                        active ? 'text-accent-500' : 'text-ink-primary'
                      )}
                    >
                      {c.name || c.id.replace('COMP#', '')}
                    </p>
                    <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-meta">
                      {c.status || 'unknown'}
                    </p>
                  </div>
                  {active && <Check className="h-3.5 w-3.5 shrink-0 text-accent-500" strokeWidth={3} />}
                </button>
              );
            })}
            {hiddenCount > 0 && (
              <button
                onClick={() => setShowAll(true)}
                className="mt-0.5 flex w-full items-center justify-center gap-2 rounded-md px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink-secondary transition-colors hover:bg-white/[0.04] hover:text-ink-primary"
              >
                Show {hiddenCount} more
              </button>
            )}
          </div>
          {scoped && entryOrg && (
            <div className="px-3 pb-2">
              <button
                onClick={() => { clearEntryOrg(); }}
                className="flex w-full items-center gap-2 py-1 text-left font-mono text-[10px] uppercase tracking-wider text-ink-meta transition-colors hover:text-ink-primary"
                title="Lift the org filter and browse every competition on the platform"
              >
                <Compass className="h-3 w-3" strokeWidth={2.5} />
                Explore more on GG Anvil
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
